import React, { createContext, useState, useEffect, ReactNode } from 'react';
import type { Environment, EnvironmentContextType } from '../types/environment';
import { ENVIRONMENT_STORAGE_KEY } from '../types/environment';

export const EnvironmentContext = createContext<EnvironmentContextType | undefined>(undefined);

interface EnvironmentProviderProps {
  children: ReactNode;
}

export const EnvironmentProvider: React.FC<EnvironmentProviderProps> = ({ children }) => {
  const [environment, setEnvironmentState] = useState<Environment>('sandbox');
  const [showProductionWarning, setShowProductionWarning] = useState(false);
  const [pendingEnvironment, setPendingEnvironment] = useState<Environment | null>(null);

  // Load environment from sessionStorage on mount
  useEffect(() => {
    const stored = sessionStorage.getItem(ENVIRONMENT_STORAGE_KEY);
    if (stored === 'sandbox' || stored === 'production') {
      setEnvironmentState(stored);
    }
  }, []);

  const setEnvironment = (env: Environment) => {
    // If switching to production, show warning first
    if (env === 'production' && environment !== 'production') {
      setPendingEnvironment(env);
      setShowProductionWarning(true);
      return;
    }

    // Otherwise, set directly
    setEnvironmentState(env);
    sessionStorage.setItem(ENVIRONMENT_STORAGE_KEY, env);
  };

  const confirmProductionSwitch = () => {
    if (pendingEnvironment === 'production') {
      setEnvironmentState('production');
      sessionStorage.setItem(ENVIRONMENT_STORAGE_KEY, 'production');
    }
    setShowProductionWarning(false);
    setPendingEnvironment(null);
  };

  const cancelProductionSwitch = () => {
    setShowProductionWarning(false);
    setPendingEnvironment(null);
  };

  const toggleEnvironment = () => {
    const newEnv: Environment = environment === 'sandbox' ? 'production' : 'sandbox';
    setEnvironment(newEnv);
  };

  const value: EnvironmentContextType = {
    environment,
    setEnvironment,
    toggleEnvironment,
    showProductionWarning,
    confirmProductionSwitch,
    cancelProductionSwitch,
  };

  return <EnvironmentContext.Provider value={value}>{children}</EnvironmentContext.Provider>;
};
